/** @file refreshActions: after a commit, resync the document, rebuild the preview, refetch lint. */
import type { EditorActions } from '../state/editorTypes';
import { asPose3DError } from './asPose3DError';
import { errorText } from './errorText';
import { publishQueue } from './publishQueue';
import type { ServerContext } from './serverContext';
import { toStoredPuppet } from './toStoredPuppet';

/** The refresh half of the server store: the public actions plus `refreshNow` (the job the schedule debounces). */
export type RefreshActions = Pick<EditorActions, 'refreshPreview' | 'refreshLint'> & { refreshNow: () => Promise<void> };

/**
 * Local application cannot reproduce server-minted ids (the server's seed is random), so after any commit the
 * document is refetched once nothing is pending, and only when the server is not behind the confirmed revision.
 */
async function resync(ctx: ServerContext, id: string, token: number): Promise<void> {
  if (!ctx.dirty || ctx.queue === null || ctx.queue.pending() > 0) return;
  const wire = await ctx.api.puppets.get(id);
  const q = ctx.queue;
  if (token !== ctx.token || q.pending() > 0 || wire.revision < q.confirmed().revision) return;
  q.reset(toStoredPuppet(wire));
  ctx.dirty = false;
  publishQueue(ctx);
}

async function buildPreview(ctx: ServerContext, id: string, token: number): Promise<void> {
  const loader = ctx.previewLoader;
  if (loader === null) return;
  ctx.set((s) => ({ preview: { ...s.preview, loading: true } }));
  try {
    const bytes = await ctx.api.puppets.exportBundle(id);
    const revision = ctx.queue?.confirmed().revision ?? -1;
    const bundle = await loader({ kind: 'zip', bytes });
    if (token === ctx.token) ctx.set({ preview: { bundle, revision, loading: false, error: null } });
  } catch (e) {
    if (token === ctx.token) ctx.set((s) => ({ preview: { ...s.preview, loading: false, error: errorText(asPose3DError(e)) } }));
  }
}

async function fetchLint(ctx: ServerContext): Promise<void> {
  const { puppet, playhead } = ctx.get();
  const id = puppet.id;
  if (id === null) return;
  const token = ctx.token;
  try {
    const reply = await ctx.api.puppets.lint(id, playhead.clip === null ? {} : { clip: playhead.clip });
    if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, lint: reply.warnings } }));
  } catch (e) {
    if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, lastError: errorText(asPose3DError(e)) } }));
  }
}

/** `refreshPreview` waits for the debounced job (a no-op without a preview loader); `refreshLint` fetches at once. */
export function refreshActions(ctx: ServerContext): RefreshActions {
  const refreshNow = async (): Promise<void> => {
    const id = ctx.get().puppet.id;
    if (id === null) return;
    const token = ctx.token;
    try {
      await resync(ctx, id, token);
    } catch (e) {
      if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, lastError: errorText(asPose3DError(e)) } }));
    }
    await buildPreview(ctx, id, token);
    await fetchLint(ctx);
  };
  return {
    refreshNow,
    refreshPreview: () => (ctx.previewLoader === null ? Promise.resolve() : ctx.schedule.schedule()),
    refreshLint: () => fetchLint(ctx),
  };
}
